import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import ewiseLogo from "../../assets/e-wise-logo.png";
import { isMentor, workspaceHome } from "../../domains/prototype/data/user-management.js";
import type { ShellIdentityProps, Workspace } from "../types/shell.js";
import { ProfileMenu } from "./ProfileMenu.js";
import { collapsedItemLimit, navigationFor, SidebarLink, SidebarMoreMenu } from "./sidebar-nav.js";
import { fadeClass, useSidebarTransition } from "./sidebar-state.js";

interface AppSidebarProps extends ShellIdentityProps {
  expanded: boolean;
  onPersonaChange: (id: string) => void;
  pathname: string;
  workspace: Workspace;
}

function initials(name: string): string {
  const parts = name
    .split(/\s+/)
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length >= 2) return `${parts[0]![0]!}${parts[1]![0]!}`.toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

export function AppSidebar({
  bootstrap,
  expanded,
  onPersonaChange,
  persona,
  pathname,
  workspace,
}: AppSidebarProps) {
  const { t } = useTranslation();
  const { shown, labelsVisible, onTransitionEnd } = useSidebarTransition(expanded);
  const fade = fadeClass(labelsVisible);

  const mentor = isMentor(persona);
  const home = workspaceHome(persona);
  const admin = workspace === "admin";
  const title = admin ? t("common.digitalColleague") : bootstrap.organisation.name;
  const subtitle = admin ? t("common.ewise") : bootstrap.organisation.customerName;
  const roleTitle =
    persona.kind === "employee"
      ? bootstrap.roles.find((role) => role.id === persona.roleId)?.title
      : persona.title;
  const showHeadings = workspace !== "employee";
  const sections = navigationFor(workspace, mentor);
  const items = sections.flatMap((section) => section.items);
  const collapsedOverflow = !shown && items.length > collapsedItemLimit;

  return (
    <aside
      className={`relative z-40 hidden h-dvh shrink-0 flex-col bg-sidebar text-sidebar-foreground transition-[width] duration-200 ease-out motion-reduce:transition-none md:flex ${shown ? "w-72" : "w-[4.75rem]"}`}
      onTransitionEnd={onTransitionEnd}
    >
      <Link
        aria-label={title}
        className={`mt-3 mb-4 flex min-h-10 shrink-0 items-center gap-3 overflow-hidden rounded-xl focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-sidebar-foreground/60 ${shown ? "mx-3" : "justify-center"}`}
        data-control-id="shell.logo.desktop"
        to={home}
      >
        {admin ? (
          <img alt="" className="size-10 shrink-0 rounded-xl object-cover" src={ewiseLogo} />
        ) : (
          <span className="grid size-10 shrink-0 place-items-center rounded-xl border border-sidebar-foreground/30 bg-sidebar-accent-foreground/50 text-sm font-medium tracking-wide">
            {initials(title)}
          </span>
        )}
        {shown ? (
          <span aria-hidden="true" className={`min-w-0 flex-1 ${fade}`}>
            <span className={`block font-medium leading-tight ${admin ? "text-sm" : "truncate text-base"}`}>
              {title}
            </span>
            <span className="block truncate text-xs text-sidebar-foreground/75">{subtitle}</span>
          </span>
        ) : null}
      </Link>

      <nav
        aria-label={t("nav.primaryNavigation")}
        className={`flex min-h-0 flex-1 flex-col pb-4 ${shown ? "overflow-x-hidden overflow-y-auto px-3" : ""}`}
      >
        {collapsedOverflow ? (
          <div className="flex flex-col gap-1">
            {items.slice(0, collapsedItemLimit).map((item) => (
              <SidebarLink expanded={false} item={item} key={item.to} labelsVisible={labelsVisible} />
            ))}
            <SidebarMoreMenu
              items={items.slice(collapsedItemLimit)}
              labelsVisible={labelsVisible}
              pathname={pathname}
            />
          </div>
        ) : (
          sections.map((section, index) => (
            <section className="flex flex-col gap-1" key={section.labelKey ?? "ungrouped"}>
              {showHeadings && section.labelKey ? (
                shown ? (
                  <h2 className="flex h-9 shrink-0 items-end overflow-hidden px-3 pb-2 text-[11px] font-medium tracking-wide whitespace-nowrap text-sidebar-foreground/60 uppercase">
                    <span className={fade}>{t(section.labelKey)}</span>
                  </h2>
                ) : index > 0 ? (
                  <div aria-hidden="true" className="mx-3 my-2 h-px shrink-0 bg-sidebar-foreground/25" />
                ) : null
              ) : null}
              {section.items.map((item) => (
                <SidebarLink expanded={shown} item={item} key={item.to} labelsVisible={labelsVisible} />
              ))}
            </section>
          ))
        )}
      </nav>

      <div className={shown ? "px-3 pt-2 pb-3" : "pt-2 pb-3"}>
        <ProfileMenu
          expanded={shown}
          labelsVisible={labelsVisible}
          onPersonaChange={onPersonaChange}
          persona={persona}
          personas={bootstrap.personas}
          roleTitle={roleTitle}
        />
      </div>
    </aside>
  );
}
